import { Checkbox, Tooltip } from 'antd';
import { useMemo } from 'react';

import { CUSTOM_LENS_TOOLTIP_PROPS } from 'components/molecules/PPA/Modals/CustomLense/customLens.ui';
import {
    DEFAULT_LENS_DIMENSION_SELECTION_COUNT,
    getCatalogSectionItems,
    getCatalogSelectionSummary,
    getDimensionSelectionLimitState,
    getDimensionsSection,
    getKpisSection,
    getSystemColumnItems,
    LENS_CATALOG_SECTION_KEYS,
    LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP,
    isLensCatalogApplyEnabled,
    isLensCatalogResetEnabled,
    setKpiSectionSelection,
    shouldBlockDimensionSelection,
    toggleCatalogItemSelection,
} from './scenarioLensCatalog.helpers';
import type { LensCatalogData, LensCatalogItem } from './scenarioLensCatalog.types';
import {
    StyledLensConfigColumn,
    StyledLensConfigColumnHeader,
    StyledLensConfigColumnList,
    StyledLensConfigItem,
    StyledLensConfigPanelBody,
    StyledLensConfigPanelHeader,
    StyledLensConfigPanelDivider,
    StyledLensConfigContentArea,
    StyledLensConfigEditButton,
    StyledLensConfigColumns,
    StyledLensConfigColumnDivider,
    StyledLensConfigSystemRow,
    StyledLensConfigSystemItem,
    StyledLensConfigFooter,
    StyledLensFooterApplyButton,
    StyledLensFooterResetButton,
} from './ScenarioLensSection.styles';

const LAST_ACTIVE_LENS_TOOLTIP = 'At least one lens must be active';

type LensConfigurationPanelProps = {
    catalog: LensCatalogData;
    baselineCatalog?: LensCatalogData | null;
    onChange: (catalog: LensCatalogData) => void;
    onEdit?: () => void;
    onApply?: () => void;
    onReset?: () => void;
    readOnly?: boolean;
    /** True when this is the only currently active (selected) lens. */
    isLastActiveLens?: boolean;
};

type ConfigColumnProps = {
    title: string;
    items: LensCatalogItem[];
    uiRole: 'DIMENSION' | 'KPI';
    onToggle: (catalogId: number, isSelected: boolean) => void;
    onToggleAll?: (isSelected: boolean) => void;
    showSelectAll?: boolean;
    readOnly?: boolean;
    /**
     * True when this lens is the only active one. The last selected item in each
     * column is protected from being unchecked so at least 1 dimension and 1 KPI
     * always remain active.
     */
    isLastActiveLens?: boolean;
};

const ConfigColumn = ({
    title,
    items,
    uiRole,
    onToggle,
    onToggleAll,
    showSelectAll = false,
    readOnly = false,
    isLastActiveLens = false,
}: ConfigColumnProps) => {
    const isDimensionColumn = uiRole === 'DIMENSION';

    const selectableItems = useMemo(() => items.filter((item) => !item.is_disabled), [items]);

    const dimensionLimitState = useMemo(
        () => (isDimensionColumn ? getDimensionSelectionLimitState(selectableItems) : null),
        [isDimensionColumn, selectableItems],
    );

    const { label: selectionLabel } = useMemo(
        () =>
            getCatalogSelectionSummary(
                selectableItems,
                isDimensionColumn ? DEFAULT_LENS_DIMENSION_SELECTION_COUNT : undefined,
            ),
        [selectableItems, isDimensionColumn],
    );

    const selectedCount = useMemo(
        () => selectableItems.filter((item) => item.is_selected).length,
        [selectableItems],
    );

    const allSelected = selectableItems.length > 0 && selectedCount === selectableItems.length;
    const someSelected = selectedCount > 0;

    if (selectableItems.length === 0) {
        return null;
    }

    return (
        <StyledLensConfigColumn>
            <StyledLensConfigColumnHeader uiRole={uiRole}>
                {isDimensionColumn ? (
                    <>
                        {title}
                        <span className="column-count"> ({selectionLabel})</span>
                    </>
                ) : (
                    title
                )}
            </StyledLensConfigColumnHeader>
            <StyledLensConfigColumnList>
                {showSelectAll && onToggleAll ? (
                    <StyledLensConfigItem isSelected={allSelected} isDisabled={false}>
                        <Checkbox
                            checked={allSelected}
                            indeterminate={someSelected && !allSelected}
                            disabled={readOnly || selectableItems.length === 0}
                            onChange={(event) => onToggleAll(event.target.checked)}
                            className="lens-config-item-checkbox !m-0"
                        >
                            (All)
                        </Checkbox>
                    </StyledLensConfigItem>
                ) : null}
                {selectableItems.map((item) => {
                    const isDimensionLimitBlocked =
                        dimensionLimitState != null &&
                        shouldBlockDimensionSelection(item, dimensionLimitState.isLimitReached);

                    // Only the final selected item is protected so at least 1 always remains.
                    const isLastSelected = isLastActiveLens && item.is_selected && selectedCount === 1;

                    const isItemDisabled = readOnly || item.is_disabled || isDimensionLimitBlocked || isLastSelected;

                    const tooltipTitle = isLastSelected
                        ? LAST_ACTIVE_LENS_TOOLTIP
                        : item.is_disabled
                          ? (item.disabled_reason ?? 'Unavailable')
                          : isDimensionLimitBlocked
                            ? LENS_DIMENSION_SELECTION_LIMIT_TOOLTIP
                            : undefined;

                    return (
                        <Tooltip
                            key={item.catalog_id}
                            {...(tooltipTitle ? CUSTOM_LENS_TOOLTIP_PROPS : {})}
                            title={tooltipTitle}
                        >
                            <span className="block w-full">
                                <StyledLensConfigItem isDisabled={isItemDisabled} isSelected={item.is_selected}>
                                    <Checkbox
                                        checked={item.is_selected}
                                        disabled={isItemDisabled}
                                        onChange={(event) => onToggle(item.catalog_id, event.target.checked)}
                                        className="lens-config-item-checkbox !m-0"
                                    >
                                        {item.display_name}
                                    </Checkbox>
                                </StyledLensConfigItem>
                            </span>
                        </Tooltip>
                    );
                })}
            </StyledLensConfigColumnList>
        </StyledLensConfigColumn>
    );
};

const LensConfigurationPanel = ({
    catalog,
    baselineCatalog = null,
    onChange,
    onEdit,
    onApply,
    onReset,
    readOnly = false,
    isLastActiveLens = false,
}: LensConfigurationPanelProps) => {
    const canApply = useMemo(
        () => isLensCatalogApplyEnabled(catalog, baselineCatalog),
        [catalog, baselineCatalog],
    );
    const canReset = useMemo(
        () => isLensCatalogResetEnabled(catalog, baselineCatalog),
        [catalog, baselineCatalog],
    );

    const systemColumnItems = getSystemColumnItems(catalog);
    const dimensionsSection = getDimensionsSection(catalog);
    const kpisSection = getKpisSection(catalog);
    const dimensionItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.DIMENSIONS);
    const kpiItems = getCatalogSectionItems(catalog, LENS_CATALOG_SECTION_KEYS.KPIS);
    const dimensionsTitle = dimensionsSection?.section_name ?? 'Dimensions';
    const kpisTitle = kpisSection?.section_name ?? 'User Inputs / KPIs';

    const handleToggle = (catalogId: number, isSelected: boolean) => {
        onChange(toggleCatalogItemSelection(catalog, catalogId, isSelected));
    };

    const handleToggleAllKpis = (isSelected: boolean) => {
        let updatedCatalog = setKpiSectionSelection(catalog, isSelected);

        // Guard: when deselecting all KPIs on the last active lens, keep the first
        // available KPI selected so the lens always has at least 1 KPI.
        if (!isSelected && isLastActiveLens) {
            const firstKpi = getCatalogSectionItems(updatedCatalog, LENS_CATALOG_SECTION_KEYS.KPIS).find(
                (item) => !item.is_disabled,
            );
            if (firstKpi) {
                updatedCatalog = toggleCatalogItemSelection(updatedCatalog, firstKpi.catalog_id, true);
            }
        }

        onChange(updatedCatalog);
    };

    return (
        <StyledLensConfigPanelBody>
            {!readOnly && onEdit ? (
                <>
                    <StyledLensConfigPanelHeader>
                        <StyledLensConfigEditButton type="button" onClick={onEdit}>
                            Edit
                        </StyledLensConfigEditButton>
                    </StyledLensConfigPanelHeader>
                    <StyledLensConfigPanelDivider aria-hidden />
                </>
            ) : null}

            <StyledLensConfigContentArea>
                {systemColumnItems.length > 0 ? (
                    <StyledLensConfigSystemRow>
                        {systemColumnItems.map((item) => {
                            const isItemDisabled = readOnly || item.is_disabled;

                            return (
                                <Tooltip
                                    key={item.catalog_id}
                                    title={item.is_disabled ? (item.disabled_reason ?? 'Unavailable') : undefined}
                                >
                                    <StyledLensConfigSystemItem
                                        isDisabled={isItemDisabled}
                                        isSelected={item.is_selected}
                                    >
                                        <Checkbox
                                            checked={item.is_selected}
                                            disabled={isItemDisabled}
                                            onChange={(event) => handleToggle(item.catalog_id, event.target.checked)}
                                            className="lens-config-item-checkbox !m-0"
                                        >
                                            {item.display_name}
                                        </Checkbox>
                                    </StyledLensConfigSystemItem>
                                </Tooltip>
                            );
                        })}
                    </StyledLensConfigSystemRow>
                ) : null}

                <StyledLensConfigColumns>
                    {dimensionsSection ? (
                        <ConfigColumn
                            title={dimensionsTitle}
                            items={dimensionItems}
                            uiRole="DIMENSION"
                            onToggle={handleToggle}
                            readOnly={readOnly}
                            isLastActiveLens={isLastActiveLens}
                        />
                    ) : null}
                    {dimensionsSection && kpisSection ? (
                        <StyledLensConfigColumnDivider aria-hidden />
                    ) : null}
                    {kpisSection ? (
                        <ConfigColumn
                            title={kpisTitle}
                            items={kpiItems}
                            uiRole="KPI"
                            onToggle={handleToggle}
                            onToggleAll={handleToggleAllKpis}
                            showSelectAll
                            readOnly={readOnly}
                            isLastActiveLens={isLastActiveLens}
                        />
                    ) : null}
                </StyledLensConfigColumns>
            </StyledLensConfigContentArea>

            {!readOnly && onApply && onReset ? (
                <>
                    <StyledLensConfigPanelDivider aria-hidden />
                    <StyledLensConfigFooter>
                        <StyledLensFooterResetButton disabled={!canReset} onClick={onReset}>
                            Reset
                        </StyledLensFooterResetButton>
                        <StyledLensFooterApplyButton type="primary" disabled={!canApply} onClick={onApply}>
                            Apply
                        </StyledLensFooterApplyButton>
                    </StyledLensConfigFooter>
                </>
            ) : null}
        </StyledLensConfigPanelBody>
    );
};

export default LensConfigurationPanel;
